import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { BrandWordmark } from "@/components/BrandWordmark";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { MfaPanel } from "@/components/auth/MfaPanel";
import { InstitutionSuggestionList } from "@/components/ui/InstitutionSuggestionList";
import { Button, Field, Input, Panel } from "@/components/ui";
import { runtimeConfig } from "@/lib/runtimeConfig";
import { authApi } from "@/lib/api";
import { toast } from "sonner";
import { getAccessRequestSuccessMessage, getAuthErrorMessage } from "@/lib/authMessages";
import { consumeSessionEndedNotice } from "@/lib/sessionNotice";
import {
  isAuthRoute,
  isSchoolAdminUser,
  isTeacherUser,
  isTrainingAdminUser,
  normalizePath,
} from "@/lib/userRoutes";
import { getHomeRoute } from "@/lib/roleRouter";
import { useSetupGate } from "@/features/first-run/useSetupGate";

/**
 * NAV-SWEEP-1b (N3) — validate the post-login return target before we navigate to it.
 *
 * `state.from` reaches us through router state, so it is only ever written by our own ProtectedRoute;
 * it is still treated as untrusted input. A same-origin, single-leading-slash path is the ONLY shape
 * accepted: "//evil.com" (protocol-relative) and "/\evil.com" (backslash variant) are open redirects
 * and are rejected, as is anything that would land back on an auth screen and loop. Search and hash
 * are preserved so a deep link survives the round trip.
 *
 * @returns a safe "/path?query#hash" string, or null to fall through to the role home.
 */
export function safeReturnPath(from) {
  if (typeof from !== "string") return null;
  const raw = from.trim();
  if (!raw.startsWith("/")) return null;
  if (raw.startsWith("//") || raw.startsWith("/\\")) return null;

  const [pathAndQuery, ...hashParts] = raw.split("#");
  const [pathname, ...queryParts] = pathAndQuery.split("?");
  const path = normalizePath(pathname);

  if (path === "/" || isAuthRoute(path)) return null;

  const search = queryParts.length ? `?${queryParts.join("?")}` : "";
  const hash = hashParts.length ? `#${hashParts.join("#")}` : "";
  return `${path}${search}${hash}`;
}

// AUTH-WAY-1 (FO5) — the 44px floor, as a HIT AREA.
// `px-3 py-2` measured 36.0px (the idiom TAP-TARGET-1's header records for py-2). `min-h-[44px]`
// with inline-flex centring raises the target without pinning a height, exactly as `.cv-btn` does;
// the floor is a minimum so a wrapped label still grows. This is a raw <button> rather than
// <Button> because the segmented control has its own resting/active treatment, so it takes the
// floor directly instead of inheriting it from the token.
function SegmentButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "inline-flex min-h-[44px] items-center justify-center rounded-lg px-3 py-2 transition-colors",
        active
          ? "bg-white text-slate-900 shadow-sm font-semibold"
          : "text-slate-500 hover:text-slate-700",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

export function AuthPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    initializing,
    login,
    register,
    requestAccessAsync,
    requestPasswordResetAsync,
    confirmPasswordResetAsync,
    loggingIn,
    registering,
    requestingAccess,
    requestingPasswordReset,
    confirmingPasswordReset,
    pendingMfa,
  } = useAuth();

  const isDemo = runtimeConfig.demoMode;
  const approvalRequired = runtimeConfig.registrationApprovalRequired;
  // FIX-2 Item C: the marketing site deep-links Sign up to app.cognivio.live/signup. Initialize the
  // mode from the entry path (or ?mode=signup) so that link opens directly in signup; everything
  // else defaults to login. setMode still toggles freely once mounted.
  const [mode, setMode] = useState(() => {
    const wantsSignup =
      location.pathname === "/signup" ||
      new URLSearchParams(location.search).get("mode") === "signup";
    return wantsSignup ? "signup" : "login";
  });
  const [showPasswordResetRequest, setShowPasswordResetRequest] = useState(false);
  const [accessRequestSubmitted, setAccessRequestSubmitted] = useState(false);
  const [accessRequestNotice, setAccessRequestNotice] = useState("");
  const [accessRequestError, setAccessRequestError] = useState("");
  const [accessType, setAccessType] = useState("teacher");
  const [institutionType, setInstitutionType] = useState("school");
  const [form, setForm] = useState({
    email: "",
    password: "",
    password_confirm: "",
    name: "",
    organization_name: "",
    school_name: "",
    requested_manager_email: "",
  });
  const authFormRef = useRef(null);

  const nameInputId = "auth-name";
  const emailInputId = "auth-email";
  const passwordInputId = "auth-password";
  const organizationInputId = "auth-organization";
  const subgroupInputId = "auth-subgroup";
  const managerEmailInputId = "auth-manager-email";
  const passwordConfirmInputId = "auth-password-confirm";

  const resetToken = useMemo(
    () => new URLSearchParams(location.search).get("reset_token") || "",
    [location.search]
  );
  // PR-3a — the activation link lands here as ?invite_token=. It reuses the reset-confirm set-a-password
  // form wholesale (same fields, same validation); only the submit target and the copy differ.
  const inviteToken = useMemo(
    () => new URLSearchParams(location.search).get("invite_token") || "",
    [location.search]
  );
  const [activatingInvite, setActivatingInvite] = useState(false);

  /**
   * AUTH-WAY-1 (FO3) — why the session ended, read once at mount.
   *
   * Read in a lazy initialiser rather than an effect: the notice is consumed (read-and-cleared) on
   * the way out of storage, and an effect under React 18 StrictMode runs twice in development,
   * which would consume it on the first pass and render nothing on the second. The bug that causes
   * is invisible in production and maddening in dev, so the read happens exactly once, here.
   */
  const [sessionNotice, setSessionNotice] = useState(() => consumeSessionEndedNotice());

  const isInviteActivateMode = Boolean(inviteToken);
  const isResetConfirmMode = Boolean(resetToken) || isInviteActivateMode;
  const isResetRequestMode = mode === "login" && showPasswordResetRequest && !isResetConfirmMode;

  // ONB-2: resolve the post-login LANDING role for the guided-setup gate. Admins land on the
  // /onboarding checklist, teachers on /my-profile, while required setup is incomplete.
  const setupRole = user
    ? isSchoolAdminUser(user) || isTrainingAdminUser(user)
      ? "admin"
      : isTeacherUser(user)
        ? "teacher"
        : null
    : null;
  const setupGate = useSetupGate(setupRole);

  // NAV-SWEEP-1b (N3) — redirect-after-login. ProtectedRoute has always stashed the intended location
  // in `state.from` when it bounces an unauthenticated visitor here; nothing ever READ it, so every
  // deep link died at the login screen and dumped the user on their role home. Captured in a ref at
  // mount because the post-login navigate() replaces this entry and clears the state.
  const intendedFromRef = useRef(location.state?.from);
  const intendedPath = safeReturnPath(intendedFromRef.current);

  useEffect(() => {
    if (initializing || !user) {
      return;
    }
    // Wait for readiness before choosing the landing (avoids a home→setup flash).
    if (setupRole && setupGate.loading) {
      return;
    }
    // ONB-2: an incomplete user LANDS on their setup surface (a navigable default — the persistent
    // banner keeps setup in view thereafter, and no route is blocked). Complete users, and roles
    // with no gate, go to their normal home. Fires at the login/approved-login home-routing moment.
    // N3: setup still WINS over the intended page — an incomplete account is routed to setup exactly
    // as before; only the "complete user, no gate" landing gains the deep-link return.
    const target =
      setupRole && !setupGate.requiredComplete
        ? normalizePath(setupGate.setupHref)
        : intendedPath || normalizePath(getHomeRoute(user));
    const current = `${normalizePath(location.pathname)}${location.search || ""}`;

    if (current !== target) {
      navigate(target, { replace: true });
    }
  }, [
    initializing,
    location.pathname,
    location.search,
    navigate,
    user,
    setupRole,
    setupGate.loading,
    setupGate.requiredComplete,
    setupGate.setupHref,
    intendedPath,
  ]);

  useEffect(() => {
    if (isResetConfirmMode) {
      setMode("login");
      setShowPasswordResetRequest(false);
      setAccessRequestSubmitted(false);
    }
  }, [isResetConfirmMode]);

  /**
   * AUTH-WAY-1 (FO6) — an invitation opens in the INVITING ORGANISATION'S language.
   *
   * THE RULE: the org's language travels with the invite; the browser's is only a fallback. The
   * reason is concrete — an invitee at a Hebrew-language college opening their invitation on a laptop that
   * happens to report `en-US` currently meets an English activation screen, and the first thing the
   * product ever says to them is in the wrong language.
   *
   * WHAT THIS HALF DOES: honours a `lang` hint on the activation URL and makes it stick (i18n
   * persists the change, so the choice carries into the session that follows). It is scoped to the
   * invite path on purpose — a general `?lang=` override on /login would be a language-switch vector
   * on a public page for no requirement anyone has stated — and it accepts only the two supported
   * languages, so an unknown value falls through to detection rather than blanking the UI.
   *
   * WHAT IT CANNOT DO: make the backend send the hint. `_build_trainee_invite_url` emits
   * `/login?invite_token=<token>` and nothing else, and there is no pre-auth endpoint that would let
   * this page look the organisation up before activation — the token is single-use and only
   * `POST /auth/invite/activate` consumes it. Carrying the org's language is therefore a backend
   * change (PREF-LANG-1), outside this run's territory. This half is written so that the day the
   * link gains `&lang=he`, the screen is already Hebrew with no further frontend work.
   */
  useEffect(() => {
    if (!isInviteActivateMode) return;
    const hint = String(new URLSearchParams(location.search).get("lang") || "").toLowerCase();
    if (!hint) return;
    const resolved = hint.startsWith("he") ? "he" : hint.startsWith("en") ? "en" : null;
    if (!resolved || resolved === i18n.language) return;
    i18n.changeLanguage(resolved);
  }, [isInviteActivateMode, location.search, i18n]);

  const derivedRole = useMemo(() => {
    if (accessType === "administrator") {
      return institutionType === "training" ? "training_admin" : "school_admin";
    }

    return "teacher";
  }, [accessType, institutionType]);

  const isTeacherRole = derivedRole === "teacher";
  const requiresOrganizationFields = mode === "signup" && !isDemo;
  const showInstitutionTypeRubric = !isDemo && (mode === "signup" || accessType === "administrator");
  const requiresSubgroupName = requiresOrganizationFields && institutionType === "school";
  const showsSubgroupField = requiresOrganizationFields;
  const requiresManagerEmail = requiresOrganizationFields && isTeacherRole;

  const roleLabel = mode === "login" ? t("auth.loginRoleLabel") : t("auth.signUpRoleLabel");
  const institutionTypeLabel = mode === "login" ? t("auth.loginInstitutionTypeLabel") : t("auth.signUpInstitutionTypeLabel");

  const roleHint = useMemo(() => {
    if (mode === "login") {
      return accessType === "administrator"
        ? t("auth.loginAdministratorHint")
        : t("auth.loginTeacherHint");
    }

    if (accessType === "administrator") {
      return institutionType === "training"
        ? t("auth.signUpAdministratorTrainingHint")
        : t("auth.signUpAdministratorSchoolHint");
    }

    return institutionType === "training"
      ? t("auth.signUpTeacherTrainingHint")
      : t("auth.signUpTeacherSchoolHint");
  }, [accessType, institutionType, mode, t]);

  const approvalDescription = useMemo(() => {
    if (accessType === "administrator") {
      return institutionType === "training"
        ? t("auth.approvalRequiredTrainingAdminDescription")
        : t("auth.approvalRequiredSchoolAdminDescription");
    }

    return institutionType === "training"
      ? t("auth.approvalRequiredTrainingTeacherDescription")
      : t("auth.approvalRequiredTeacherDescription");
  }, [accessType, institutionType, t]);

  const organizationFieldLabel = institutionType === "training"
    ? t("auth.trainingOrganizationName")
    : t("auth.schoolOrganizationName");
  const organizationFieldHint = institutionType === "training"
    ? t("auth.trainingOrganizationHint")
    : t("auth.schoolOrganizationHint");
  const subgroupFieldLabel = institutionType === "training"
    ? t("auth.programOrCohortName")
    : t("auth.schoolName");
  const subgroupFieldHint = institutionType === "training"
    ? t("auth.programOrCohortHint")
    : t("auth.schoolNameHint");
  const managerFieldLabel = institutionType === "training"
    ? t("auth.requestedTrainingManagerEmail")
    : t("auth.requestedSchoolManagerEmail");
  const managerFieldHint = institutionType === "training"
    ? t("auth.requestedTrainingManagerEmailHint")
    : t("auth.requestedSchoolManagerEmailHint");

  const summaryItems = [
    { label: t("auth.name"), value: form.name || "—" },
    { label: t("auth.email"), value: form.email || "—" },
    { label: t("auth.signupSummaryAccess"), value: t(`auth.signupAccessMap.${accessType}`) },
    { label: t("auth.signupSummaryInstitutionType"), value: t(`auth.institutionTypeMap.${institutionType}`) },
    { label: t("auth.signupSummaryDashboard"), value: t(`auth.derivedRoleMap.${derivedRole}`) },
    { label: t("auth.signupSummaryOrganization"), value: form.organization_name || "—" },
    {
      label: institutionType === "training" ? t("auth.signupSummaryProgram") : t("auth.signupSummarySchool"),
      value: form.school_name || "—",
    },
    {
      label: t("auth.signupSummaryLinkedAdministrator"),
      value: form.requested_manager_email || "—",
    },
  ];

  const { data: institutionLookupRes } = useQuery({
    queryKey: ["signup-institution-lookup", institutionType, form.organization_name],
    queryFn: () =>
      authApi
        .institutionLookup({
          organization_type: institutionType,
          q: form.organization_name.trim(),
          limit: 6,
        })
        .then((res) => res.data),
    enabled: mode === "signup" && !isDemo && form.organization_name.trim().length >= 2,
  });

  const applyInstitutionSuggestion = (suggestion) => {
    setForm((current) => ({
      ...current,
      organization_name: suggestion.organization_name || current.organization_name,
      school_name: suggestion.school_name || current.school_name,
      requested_manager_email:
        isTeacherRole && suggestion.manager_email
          ? suggestion.manager_email
          : current.requested_manager_email,
    }));
  };

  const reconcileFormFromDom = () => {
    if (!authFormRef.current) {
      return form;
    }

    const formData = new FormData(authFormRef.current);
    const domValues = {
      email: String(formData.get("email") || "").trim(),
      password: String(formData.get("password") || ""),
      password_confirm: String(formData.get("password_confirm") || ""),
      name: String(formData.get("name") || "").trim(),
      organization_name: String(formData.get("organization_name") || "").trim(),
      school_name: String(formData.get("school_name") || "").trim(),
      requested_manager_email: String(formData.get("requested_manager_email") || "").trim(),
    };

    const nextForm = {
      ...form,
      ...Object.fromEntries(
        Object.entries(domValues).map(([key, value]) => [
          key,
          value || form[key] || "",
        ])
      ),
    };

    setForm(nextForm);
    return nextForm;
  };

  const buildSignupPayload = (sourceForm) => ({
    email: sourceForm.email,
    password: sourceForm.password,
    name: sourceForm.name,
    organization_type: institutionType,
    organization_name: sourceForm.organization_name,
    ...(sourceForm.school_name ? { school_name: sourceForm.school_name } : {}),
    ...(isTeacherRole && sourceForm.requested_manager_email
      ? { requested_manager_email: sourceForm.requested_manager_email }
      : {}),
    ...(!isDemo ? { role: derivedRole } : {}),
  });

  const validateRequestAccessPayload = (payload) => {
    const missingIdentityFields = ["name", "email", "password"].filter(
      (field) => !String(payload[field] || "").trim()
    );

    if (missingIdentityFields.length > 0) {
      return "Please complete your name, email, and password before submitting.";
    }

    if (requiresOrganizationFields && !String(payload.organization_name || "").trim()) {
      return "Please enter your institution or organization name before submitting.";
    }

    if (requiresSubgroupName && !String(payload.school_name || "").trim()) {
      return "Please enter your school name before submitting.";
    }

    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    // FO3 — the reader has acted on the expiry notice; it has done its job and should not sit above
    // a fresh error from this submission, where it would read as part of the new failure.
    setSessionNotice(null);
    const currentForm = reconcileFormFromDom();

    if (isResetConfirmMode) {
      if (currentForm.password !== currentForm.password_confirm) {
        return;
      }

      if (isInviteActivateMode) {
        // PR-3a — consume the single-use invite token and set the password. On success the account is
        // active; the invitee signs in on this same page and lands in the app.
        setActivatingInvite(true);
        try {
          await authApi.activateInvite({ token: inviteToken, password: currentForm.password });
          setForm((current) => ({ ...current, password: "", password_confirm: "" }));
          toast.success(t("auth.activateInviteSuccess"));
          navigate("/login", { replace: true });
        } catch (error) {
          toast.error(getAuthErrorMessage(error, t("auth.activateInviteFailed")));
        } finally {
          setActivatingInvite(false);
        }
        return;
      }

      try {
        await confirmPasswordResetAsync({
          token: resetToken,
          password: currentForm.password,
        });
        setForm((current) => ({
          ...current,
          password: "",
          password_confirm: "",
        }));
        navigate("/login", { replace: true });
      } catch {
        return;
      }

      return;
    }

    if (isResetRequestMode) {
      try {
        await requestPasswordResetAsync({ email: currentForm.email });
        setShowPasswordResetRequest(false);
      } catch {
        return;
      }

      return;
    }

    const signupPayload = buildSignupPayload(currentForm);

    if (mode === "signup" && approvalRequired) {
      setAccessRequestNotice("");
      setAccessRequestError("");
      const validationMessage = validateRequestAccessPayload(signupPayload);
      if (validationMessage) {
        setAccessRequestError(validationMessage);
        return;
      }
      try {
        const res = await requestAccessAsync(signupPayload);
        setForm((current) => ({
          ...current,
          password: "",
        }));
        setAccessRequestSubmitted(true);
        setAccessRequestNotice(getAccessRequestSuccessMessage(res?.data, ""));
        if (res?.data?.status === "approved") {
          setMode("login");
          setAccessRequestSubmitted(false);
        }
      } catch (error) {
        setAccessRequestError(getAuthErrorMessage(error, t("auth.requestAccessFailed")));
        return;
      }

      return;
    }

    // UX-5: login no longer sends a role — the backend resolves role from the account (confirmed),
    // and role-based routing sends each role home after the F-1 guard fix. The role/persona choice
    // is only meaningful for sign-up (registration), which keeps it.
    const payload =
      mode === "signup"
        ? signupPayload
        : {
            email: currentForm.email,
            password: currentForm.password,
          };

    const fn = mode === "signup" ? register : login;
    fn(payload);
  };

  const busy =
    loggingIn ||
    registering ||
    requestingAccess ||
    requestingPasswordReset ||
    confirmingPasswordReset ||
    activatingInvite;

  const submitLabel = isInviteActivateMode
    ? busy
      ? t("auth.activateInviteSubmitting")
      : t("auth.activateInviteSubmit")
    : isResetConfirmMode
    ? busy
      ? t("auth.resetPasswordSubmitting")
      : t("auth.resetPasswordSubmit")
    : isResetRequestMode
      ? busy
        ? t("auth.passwordResetRequestSubmitting")
        : t("auth.passwordResetRequestSubmit")
      : busy
        ? mode === "signup"
          ? approvalRequired
            ? t("auth.signingUp")
            : t("auth.creatingAccount")
          : t("auth.signingIn")
        : mode === "login"
          ? t("auth.signIn")
          : t("auth.signUpCta");

  const pageTitle = isInviteActivateMode
    ? t("auth.activateInviteTitle")
    : isResetConfirmMode
    ? t("auth.resetPasswordTitle")
    : isResetRequestMode
      ? t("auth.forgotPasswordTitle")
      : t("auth.title");

  const pageSubtitle = isInviteActivateMode
    ? t("auth.activateInviteSubtitle")
    : isResetConfirmMode
    ? t("auth.resetPasswordSubtitle")
    : isResetRequestMode
      ? t("auth.forgotPasswordSubtitle")
      : t("auth.subtitle");

  return (
    // UX-5: the airlock, in the landing's language — ivory field, a quiet corner language control,
    // a serif wordmark + short gold rule.
    <div className="relative flex min-h-screen items-center justify-center bg-ivory px-4 py-8">
      {/* AUTH-WAY-1 — `end-4` (inset-inline-end), not `right-4`. The physical property pinned the
          language control to the right edge in Hebrew too, where it sat on the side the reader's
          eye leaves last. Logical properties mirror with `dir` and need no second code path. */}
      <div className="absolute end-4 top-4 text-xs">
        <LanguageSwitcher compact />
      </div>
      <Panel as="div" className="w-full max-w-md p-8">
        {/* UX-5b: the wordmark IS the hero (serif Cognivio + gold rule); one quiet line beneath. */}
        <div className="mb-6 flex flex-col items-center text-center">
          <BrandWordmark to="/" size="lg" />
          <p className="mt-4 text-sm text-slate-600">
            {isResetRequestMode || isResetConfirmMode
              ? pageSubtitle
              : mode === "signup"
                ? t("auth.signUpSubtitle")
                : t("auth.subtitle")}
          </p>
        </div>

        {/* AUTH-WAY-1 (FO3) — the designed state for a session that ended.
            Both expiry regimes land here: the 30-minute idle window and the absolute max lifetime.
            NOT red: an expiry is routine housekeeping, not a fault of the reader's and not a system
            error, and the never-alarming law reserves red for the latter. `role="status"` with a
            polite live region for the same reason — it is announced, not interrupted with.
            The sentence itself is resolved in apiErrors against the active language, so this panel
            speaks Hebrew to a Hebrew reader without a second string here. */}
        {sessionNotice ? (
          <div
            className="mb-4 rounded-brand border border-border bg-primary-tint p-3 text-sm text-ink"
            role="status"
            aria-live="polite"
            data-testid="auth-session-ended"
            data-reason-code={sessionNotice.reason_code || ""}
          >
            {sessionNotice.message}
          </div>
        ) : null}

        {/* PRIV-2 (MFA): a login paused for a second factor replaces the whole login/signup control
            with the MFA challenge/enrollment panel until it resolves (or the user cancels). */}
        {pendingMfa ? (
          <MfaPanel />
        ) : (
        <>
        {/* UX-5b: the login/sign-up control is demoted from a 50/50 hero toggle to quiet underlined
            tabs so it stops competing with the wordmark. */}
        {!isResetConfirmMode ? (
          <div className="mb-5 flex justify-center gap-5 text-xs">
            <button
              type="button"
              onClick={() => {
                setMode("login");
                setShowPasswordResetRequest(false);
                setAccessRequestSubmitted(false);
                setAccessRequestNotice("");
                setAccessRequestError("");
              }}
              className="inline-flex min-h-[44px] items-center justify-center px-1"
            >
              {/* FO5 — the FLOOR IS ON THE BUTTON, THE UNDERLINE IS ON THE SPAN. Putting
                  `min-h-[44px]` on the element that also carries `border-b-2` would drop the gold
                  rule to the bottom of a 44px box, a finger's width below the word it underlines —
                  the control would measure right and look broken. The hit area is the button; the
                  visual treatment stays exactly the 17px it was. This is what "hit area, not box"
                  means in practice, and it is why this tab is not simply given `cv-btn`. */}
              <span
                className={`pb-0.5 ${
                  mode === "login" && !showPasswordResetRequest
                    ? "border-b-2 border-gold font-semibold text-ink"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {t("auth.loginTab")}
              </span>
            </button>
            {!isDemo && (
              <button
                type="button"
                onClick={() => {
                  setMode("signup");
                  setShowPasswordResetRequest(false);
                  setAccessRequestSubmitted(false);
                  setAccessRequestNotice("");
                  setAccessRequestError("");
                }}
                className="inline-flex min-h-[44px] items-center justify-center px-1"
              >
                {/* FO5 — see the login tab: floor on the button, underline on the span. */}
                <span
                  className={`pb-0.5 ${
                    mode === "signup"
                      ? "border-b-2 border-gold font-semibold text-ink"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {t("auth.signUpTab")}
                </span>
              </button>
            )}
          </div>
        ) : null}

        {/* UX-5: the role/persona choice is only meaningful for sign-up (registration). Login
            resolves role server-side, so the toggle no longer clutters the airlock. */}
        {mode === "signup" && !isDemo && !isResetRequestMode && !isResetConfirmMode ? (
          <div className="mb-4 space-y-4">
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                {roleLabel}
              </div>
              <div className="grid grid-cols-2 gap-2 rounded-xl bg-slate-100 p-1 text-sm">
                <SegmentButton active={accessType === "teacher"} onClick={() => setAccessType("teacher")}>
                  {t("auth.teacherRole")}
                </SegmentButton>
                <SegmentButton active={accessType === "administrator"} onClick={() => setAccessType("administrator")}>
                  {t("auth.adminRole")}
                </SegmentButton>
              </div>
            </div>

            {showInstitutionTypeRubric ? (
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {institutionTypeLabel}
                </div>
                <div className="grid grid-cols-1 gap-2 rounded-xl bg-slate-100 p-1 text-sm sm:grid-cols-2">
                  <SegmentButton active={institutionType === "school"} onClick={() => setInstitutionType("school")}>
                    {t("auth.institutionTypeSchool")}
                  </SegmentButton>
                  <SegmentButton active={institutionType === "training"} onClick={() => setInstitutionType("training")}>
                    {t("auth.institutionTypeTraining")}
                  </SegmentButton>
                </div>
                <p className="mt-2 text-xs text-slate-500">{t("auth.institutionTypeHint")}</p>
              </div>
            ) : null}

            <p className="text-xs text-slate-500">{roleHint}</p>
          </div>
        ) : null}

        {isDemo && (
          <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            <div className="font-semibold text-slate-700">{t("auth.demoLogins")}</div>
            <div className="mt-1">{t("auth.principalDemo")}</div>
            <div>{t("auth.teacherDemo")}</div>
            <div>{t("auth.trainingAdminDemo")}</div>
          </div>
        )}

        {!isDemo && approvalRequired && mode === "signup" && !isResetConfirmMode && !isResetRequestMode && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <div className="font-semibold">{t("auth.approvalRequiredTitle")}</div>
            <div className="mt-1">{approvalDescription}</div>
          </div>
        )}

        {accessRequestSubmitted && mode === "signup" && !isResetConfirmMode && !isResetRequestMode ? (
          <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            {/* AUTH-WAY-1 (item 7) keyed these three, which were hardcoded English on a page that
                ships in two languages. AUTH-STRINGS-1 removed their defaultValues: Lane 1's values
                are on main and both bundles resolve, so the fallback could only mask a missing key.
                The every-key-resolves fence in AuthPathKeys.test.js covers them now. */}
            <div className="font-semibold">
              {t("auth.accessRequestSubmittedTitle")}
            </div>
            <div className="mt-1">
              {t("auth.accessRequestSubmittedBody")}
            </div>
            {accessRequestNotice ? <div className="mt-2 text-xs">{accessRequestNotice}</div> : null}
          </div>
        ) : null}

        {accessRequestError && mode === "signup" && !isResetConfirmMode && !isResetRequestMode ? (
          <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900" role="alert">
            <div className="font-semibold">
              {t("auth.accessRequestNeedsAttentionTitle")}
            </div>
            <div className="mt-1">{accessRequestError}</div>
          </div>
        ) : null}

        <form ref={authFormRef} onSubmit={onSubmit} className="space-y-4">
          {mode === "signup" && !isDemo && !isResetConfirmMode && !isResetRequestMode && (
            <>
              <Panel className="space-y-4 border-slate-200 bg-slate-50">
                <div>
                  <div className="text-sm font-semibold text-slate-900">{t("auth.institutionDetailsTitle")}</div>
                  <p className="mt-1 text-xs text-slate-500">{t("auth.institutionDetailsDescription")}</p>
                </div>

                <Field label={organizationFieldLabel} htmlFor={organizationInputId}>
                  <Input
                    className="min-h-[44px]"
                    id={organizationInputId}
                    name="organization_name"
                    autoComplete="organization"
                    type="text"
                    required
                    value={form.organization_name}
                    onChange={(e) => setForm((f) => ({ ...f, organization_name: e.target.value }))}
                    onInput={(e) => setForm((f) => ({ ...f, organization_name: e.target.value }))}
                  />
                  <p className="mt-2 text-xs text-slate-500">{organizationFieldHint}</p>
                  <InstitutionSuggestionList
                    suggestions={institutionLookupRes?.suggestions || []}
                    title={t("auth.institutionMatchesTitle")}
                    emptyLabel={
                      form.organization_name.trim().length >= 2
                        ? t("auth.institutionMatchesEmpty")
                        : null
                    }
                    selectLabel={t("auth.useInstitutionMatch")}
                    onSelect={applyInstitutionSuggestion}
                  />
                </Field>

                {showsSubgroupField ? (
                  <Field label={subgroupFieldLabel} htmlFor={subgroupInputId}>
                    <Input
                      className="min-h-[44px]"
                      id={subgroupInputId}
                      name="school_name"
                      autoComplete="organization-title"
                      type="text"
                      required={requiresSubgroupName}
                      value={form.school_name}
                      onChange={(e) => setForm((f) => ({ ...f, school_name: e.target.value }))}
                      onInput={(e) => setForm((f) => ({ ...f, school_name: e.target.value }))}
                    />
                    <p className="mt-2 text-xs text-slate-500">{subgroupFieldHint}</p>
                  </Field>
                ) : null}

                {requiresManagerEmail ? (
                  <Field label={managerFieldLabel} htmlFor={managerEmailInputId}>
                    <Input
                      className="min-h-[44px]"
                      id={managerEmailInputId}
                      name="requested_manager_email"
                      autoComplete="email"
                      type="email"
                      value={form.requested_manager_email}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          requested_manager_email: e.target.value,
                        }))
                      }
                      onInput={(e) =>
                        setForm((f) => ({
                          ...f,
                          requested_manager_email: e.target.value,
                        }))
                      }
                    />
                    <p className="mt-2 text-xs text-slate-500">{managerFieldHint}</p>
                  </Field>
                ) : null}
              </Panel>

              <Panel className="space-y-3 border-slate-200 bg-white">
                <div>
                  <div className="text-sm font-semibold text-slate-900">{t("auth.signupSummaryTitle")}</div>
                  <p className="mt-1 text-xs text-slate-500">{t("auth.signupSummaryDescription")}</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {summaryItems.map((item) => (
                    <div key={item.label} className="rounded-xl bg-slate-50 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        {item.label}
                      </div>
                      <div className="mt-1 text-sm text-slate-700">{item.value || "—"}</div>
                    </div>
                  ))}
                </div>
              </Panel>
            </>
          )}

          {mode === "signup" && !isDemo && !isResetConfirmMode && !isResetRequestMode && (
            <Field label={t("auth.name")} htmlFor={nameInputId}>
              <Input
                className="min-h-[44px]"
                id={nameInputId}
                name="name"
                autoComplete="name"
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                onInput={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </Field>
          )}

          <Field
            label={
              isResetRequestMode
                ? t("auth.passwordResetEmailLabel")
                : t("auth.email")
            }
            htmlFor={emailInputId}
          >
            {/* AUTH-WAY-1 (FO5) — every <Input> on this page carries the floor.
                Measured at 1366 before this: 37.6px, from `cv-input`'s md size. They are the most
                TAPPED controls on the whole path — an invitee setting a password on a phone touches
                these twice before anything else — so flooring the buttons and leaving these would
                have met the letter of the order and missed its point.

                FLOORED AT THE CALL SITE, NOT IN `cv-input`. The token is shared by 23 files; raising
                it there is the right end state but it restyles every form in the product, which is a
                sweep with its own proof obligation and not this run's order. Reported as a register
                row instead of widened silently. */}
            <Input
              className="min-h-[44px]"
              id={emailInputId}
              name="email"
              autoComplete="email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              onInput={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </Field>

          {!isResetRequestMode ? (
            <Field
              label={isResetConfirmMode ? t("auth.newPassword") : t("auth.password")}
              htmlFor={passwordInputId}
            >
              <Input
                className="min-h-[44px]"
                id={passwordInputId}
                name="password"
                autoComplete={isResetConfirmMode ? "new-password" : mode === "signup" ? "new-password" : "current-password"}
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                onInput={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              />
            </Field>
          ) : null}

          {isResetConfirmMode ? (
            <Field label={t("auth.confirmNewPassword")} htmlFor={passwordConfirmInputId}>
              <Input
                className="min-h-[44px]"
                id={passwordConfirmInputId}
                name="password_confirm"
                autoComplete="new-password"
                type="password"
                required
                value={form.password_confirm || ""}
                onChange={(e) => setForm((f) => ({ ...f, password_confirm: e.target.value }))}
                onInput={(e) => setForm((f) => ({ ...f, password_confirm: e.target.value }))}
              />
            </Field>
          ) : null}

          {isResetConfirmMode && form.password && form.password_confirm && form.password !== form.password_confirm ? (
            <p className="text-xs text-rose-600">{t("auth.passwordMismatch")}</p>
          ) : null}

          <Button type="submit" disabled={busy || accessRequestSubmitted} fullWidth className="mt-2 shadow-brand">
            {submitLabel}
          </Button>
        </form>

        {mode === "login" && !isResetRequestMode && !isResetConfirmMode ? (
          <div className="mt-3 text-center">
            <button
              type="button"
              onClick={() => setShowPasswordResetRequest(true)}
              // FO5 — floored. TAP-TARGET-1's FENCE 3 excludes links INSIDE RUNNING TEXT, because a
              // 44px box around a word mid-sentence breaks the reading surface. This is not one of
              // those: it is a standalone control on its own centred line, so the exclusion does not
              // reach it and the floor applies. `inline-flex` because display:inline ignores
              // min-height outright — the class would be present and inert, which is the most
              // dangerous kind of fix.
              className="inline-flex min-h-[44px] items-center justify-center px-2 text-xs font-medium text-primary hover:text-primary/80"
            >
              {t("auth.forgotPasswordLink")}
            </button>
          </div>
        ) : null}

        {isResetRequestMode || isResetConfirmMode ? (
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => {
                setShowPasswordResetRequest(false);
                setForm((current) => ({ ...current, password: "", password_confirm: "" }));
                navigate("/login", { replace: true });
              }}
              // FO5 — floored; standalone centred control, see the forgot-password link above.
              className="inline-flex min-h-[44px] items-center justify-center px-2 text-xs font-medium text-primary hover:text-primary/80"
            >
              {t("auth.backToLogin")}
            </button>
          </div>
        ) : null}

        {!isDemo && approvalRequired && mode === "login" && !isResetRequestMode && !isResetConfirmMode ? (
          <p className="mt-4 text-center text-xs text-slate-500">
            {t("auth.needApprovalHint")}
          </p>
        ) : null}
        </>
        )}
      </Panel>
    </div>
  );
}
